import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { Task } from './types';
import { fetchTaskAssignees } from './api';

interface TaskFormDialogProps {
  open: boolean;
  task: Task | null;
  onClose: () => void;
  onSave: (payload: Record<string, any>) => Promise<void>;
  onDelete?: () => Promise<void>;
  canDelete?: boolean;
}

const statusOptions = [
  { value: 'todo', label: 'Att göra', color: 'default' as const },
  { value: 'doing', label: 'Pågår', color: 'warning' as const },
  { value: 'done', label: 'Klart', color: 'success' as const },
];

const categoryOptions = [
  { value: 'Content', label: 'Innehåll' },
  { value: 'Testing', label: 'Testning' },
  { value: 'Process', label: 'Process' },
  { value: 'Release', label: 'Release' },
];

const priorityOptions = [
  { value: 1, label: 'P1 - Kritisk', color: 'error' as const },
  { value: 2, label: 'P2 - Hög', color: 'warning' as const },
  { value: 3, label: 'P3 - Normal', color: 'info' as const },
  { value: 4, label: 'P4 - Låg', color: 'default' as const },
];

const TaskFormDialog: React.FC<TaskFormDialogProps> = ({
  open,
  task,
  onClose,
  onSave,
  onDelete,
  canDelete = true,
}) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'Process',
    priority: 3,
    status: 'todo',
    assigneeName: '',
  });
  const [saving, setSaving] = useState(false);
  const [knownAssignees, setKnownAssignees] = useState<string[]>([]);

  // Load known assignees when dialog opens
  useEffect(() => {
    if (open) {
      fetchTaskAssignees()
        .then(setKnownAssignees)
        .catch(() => setKnownAssignees([]));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (task) {
        setForm({
          title: task.title,
          description: task.description || '',
          category: task.category,
          priority: task.priority,
          status: task.status,
          assigneeName: task.assigneeName || '',
        });
      } else {
        setForm({
          title: '',
          description: '',
          category: 'Process',
          priority: 3,
          status: 'todo',
          assigneeName: '',
        });
      }
    }
  }, [open, task]);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        description: form.description,
        category: form.category,
        priority: form.priority,
        status: form.status,
        assigneeName: form.assigneeName.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!window.confirm('Är du säker på att du vill ta bort denna uppgift?')) return;
    setSaving(true);
    try {
      await onDelete();
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!task;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {isEditing ? 'Redigera uppgift' : 'Skapa ny uppgift'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditing ? 'Uppdatera uppgiftens detaljer' : 'Fyll i information om uppgiften'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Titel"
          fullWidth
          margin="dense"
          value={form.title}
          onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          required
          autoFocus
          placeholder="Vad ska göras?"
          sx={{ mt: 1 }}
        />
        <TextField
          label="Beskrivning"
          fullWidth
          margin="dense"
          multiline
          rows={3}
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Lägg till mer information (valfritt)"
        />
        
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Status & Prioritet
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
            fullWidth
            size="small"
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label={opt.label} color={opt.color} sx={{ minWidth: 70 }} />
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Prioritet"
            value={form.priority}
            onChange={(e) => setForm((prev) => ({ ...prev, priority: Number(e.target.value) }))}
            fullWidth
            size="small"
          >
            {priorityOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label={`P${opt.value}`} color={opt.color} sx={{ minWidth: 32 }} />
                  <span>{opt.label.split(' - ')[1]}</span>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Kategori
        </Typography>
        <TextField
          select
          label="Kategori"
          value={form.category}
          onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
          fullWidth
          size="small"
        >
          {categoryOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
          Tilldelad
        </Typography>
        <Autocomplete
          freeSolo
          options={knownAssignees}
          value={form.assigneeName}
          onChange={(_, newValue) => setForm((prev) => ({ ...prev, assigneeName: newValue || '' }))}
          onInputChange={(_, newInputValue) => setForm((prev) => ({ ...prev, assigneeName: newInputValue }))}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Skriv ett namn (sparas för framtida val)"
              InputProps={{
                ...params.InputProps,
                startAdornment: <PersonIcon sx={{ color: 'action.active', mr: 1 }} fontSize="small" />,
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <PersonIcon sx={{ color: 'action.active', mr: 1 }} fontSize="small" />
              {option}
            </li>
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isEditing && onDelete && canDelete && (
          <Button 
            color="error" 
            startIcon={<DeleteIcon />} 
            onClick={handleDelete}
            disabled={saving}
          >
            Ta bort
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>Avbryt</Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={saving || !form.title.trim()}
        >
          {isEditing ? 'Spara ändringar' : 'Skapa uppgift'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialog;
